import type { SchemaDescriptor } from './api-types';

export interface RecordCountIncrease {
  objectId: string;
  recordCount: number;
}

type Listener = (increase: RecordCountIncrease) => void;
const listeners = new Set<Listener>();

export function subscribeToRecordCountIncreases(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function publishRecordCountIncrease(increase: RecordCountIncrease): void {
  for (const listener of listeners) listener(increase);
}

export function recordCountIncreaseFromToolResult(
  toolName: string,
  output: unknown,
): RecordCountIncrease | null {
  if (toolName !== 'create_record' && toolName !== 'execute_import') return null;
  if (!isObject(output)) return null;
  const objectId = output.objectDefId;
  const recordCount = output.recordCount;
  if (typeof objectId !== 'string' || typeof recordCount !== 'number' || !Number.isFinite(recordCount)) {
    return null;
  }
  if (toolName === 'execute_import' && !(typeof output.created === 'number' && output.created > 0)) {
    return null;
  }
  return { objectId, recordCount };
}

export function applyLiveRecordCount(
  descriptor: SchemaDescriptor | undefined,
  increase: RecordCountIncrease,
): SchemaDescriptor | undefined {
  if (!descriptor) return descriptor;
  const object = descriptor.objects.find((candidate) => candidate.id === increase.objectId);
  if (!object || object.recordCount >= increase.recordCount) return descriptor;
  return {
    ...descriptor,
    objects: descriptor.objects.map((candidate) => (
      candidate.id === increase.objectId
        ? { ...candidate, recordCount: increase.recordCount }
        : candidate
    )),
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
